(() => {
  "use strict";

  /**
   * Guard against double-initialisation.
   *
   * Shopify re-runs section scripts in the theme editor
   * (shopify:section:load), which would otherwise stack
   * duplicate listeners and observers.
   */
  if (window.__playerTypeSync) {
    return;
  }

  /**
   * The Globo app container. Nothing in this script runs
   * unless it exists on the page.
   */
  const ROOT_SELECTOR = ".gpo-app";

  /**
   * Globo injects its form asynchronously, so the container
   * is usually absent at DOM ready. Poll for it, then give
   * up so we don't watch pages that will never have one.
   */
  const ROOT_POLL_MS = 100;
  const ROOT_WAIT_MS = 15000;

  const INPUT_SELECTOR =
    'input[data-type="buttons_multiple"][data-field-name]';

  /**
   * Matches:
   *
   * Player 1 - Type
   * Player 1 - Type - Sewn
   * Player 20 - Type
   * Player 20 - Type - Sewn
   */
  const TYPE_REGEX =
    /^Player\s+(\d+)\s+-\s+Type(?:\s+-\s+(Sewn))?$/i;

  /**
   * Globo mutates several attributes in sequence.
   * Wait for it to settle before syncing.
   */
  const SETTLE_MS = 25;

  /**
   * If both field variants are enabled at once, Globo is
   * mid-update. Retry a bounded number of times instead of
   * silently dropping the sync.
   */
  const MAX_SETTLE_ATTEMPTS = 8;

  const MODE = {
    NONE: 0,       // player hidden entirely
    AMBIGUOUS: 1,  // Globo still processing
    REGULAR: 2,
    SEWN: 3
  };

  /** player -> Set(["Home", "Away"]) */
  const playerState = new Map();

  /** Players currently being written to by us. */
  const syncingPlayers = new Set();

  /** player -> { id, attempt } */
  const syncTimers = new Map();

  /** input -> { player, sewn } | null (parse cache) */
  const metaCache = new WeakMap();

  /** player -> { regular: input[], sewn: input[] } */
  let index = null;

  let root = null;
  let observer = null;
  let rootPollId = null;

  /* ------------------------------------------------------------------ *
   * Parsing + index
   * ------------------------------------------------------------------ */

  /**
   * Parse once per element, then reuse.
   * The cache is invalidated when data-field-name changes.
   */
  function parseTypeInput(input) {
    if (metaCache.has(input)) {
      return metaCache.get(input);
    }

    let meta = null;

    if (input instanceof HTMLInputElement) {
      const fieldName = input.getAttribute("data-field-name");
      const match = fieldName && fieldName.match(TYPE_REGEX);

      if (match) {
        meta = {
          player: Number(match[1]),
          sewn: Boolean(match[2])
        };
      }
    }

    metaCache.set(input, meta);

    return meta;
  }

  /**
   * Build the player -> inputs index.
   *
   * This is the only place that touches the DOM by selector.
   * It is rebuilt only when nodes are added or renamed, not
   * when Globo toggles `disabled`.
   */
  function buildIndex() {
    const map = new Map();

    root.querySelectorAll(INPUT_SELECTOR).forEach(input => {
      const meta = parseTypeInput(input);

      if (!meta) {
        return;
      }

      let entry = map.get(meta.player);

      if (!entry) {
        entry = { regular: [], sewn: [] };
        map.set(meta.player, entry);
      }

      entry[meta.sewn ? "sewn" : "regular"].push(input);
    });

    index = map;

    return index;
  }

  function getIndex() {
    return index || buildIndex();
  }

  function invalidateIndex() {
    index = null;
  }

  /* ------------------------------------------------------------------ *
   * State
   * ------------------------------------------------------------------ */

  function getInputs(player, sewn) {
    const entry = getIndex().get(player);

    if (!entry) {
      return [];
    }

    return entry[sewn ? "sewn" : "regular"];
  }

  /**
   * Which variant of the Type field is live right now.
   *
   * Globo disables fields hidden by conditional logic, so
   * `disabled` is more reliable than CSS visibility.
   */
  function getActiveMode(player) {
    const entry = getIndex().get(player);

    if (!entry) {
      return MODE.NONE;
    }

    const isLive = input =>
      input.isConnected && !input.disabled;

    const regularActive = entry.regular.some(isLive);
    const sewnActive = entry.sewn.some(isLive);

    if (regularActive && sewnActive) {
      return MODE.AMBIGUOUS;
    }

    if (!regularActive && !sewnActive) {
      return MODE.NONE;
    }

    return sewnActive ? MODE.SEWN : MODE.REGULAR;
  }

  function savePlayerState(player, sewn) {
    const selected = new Set(
      getInputs(player, sewn)
        .filter(input => input.checked)
        .map(input => input.value)
    );

    playerState.set(player, selected);
  }

  /**
   * Write canonical selections into the active field.
   *
   * We click the real inputs rather than assigning
   * `checked`, so Globo runs its own price / add-on logic.
   */
  function applyPlayerState(player, sewn) {
    const desired = playerState.get(player);

    if (!desired) {
      return;
    }

    const inputs = getInputs(player, sewn)
      .filter(input => input.isConnected && !input.disabled);

    if (!inputs.length) {
      return;
    }

    const pending = inputs.filter(
      input => input.checked !== desired.has(input.value)
    );

    if (!pending.length) {
      return;
    }

    syncingPlayers.add(player);

    try {
      pending.forEach(input => input.click());
    } finally {
      syncingPlayers.delete(player);
    }
  }

  /* ------------------------------------------------------------------ *
   * Sync scheduling
   * ------------------------------------------------------------------ */

  function syncPlayer(player, attempt) {
    const mode = getActiveMode(player);

    if (mode === MODE.AMBIGUOUS) {
      if (attempt < MAX_SETTLE_ATTEMPTS) {
        schedulePlayerSync(player, attempt + 1);
      }
      return;
    }

    if (mode === MODE.NONE) {
      return;
    }

    const sewn = mode === MODE.SEWN;

    /**
     * First time we see this player: seed from the DOM.
     * Applying an empty state here would wipe a selection
     * the customer made before the script indexed it.
     */
    if (!playerState.has(player)) {
      savePlayerState(player, sewn);
      return;
    }

    applyPlayerState(player, sewn);
  }

  function schedulePlayerSync(player, attempt = 0) {
    const existing = syncTimers.get(player);

    if (existing) {
      clearTimeout(existing.id);
    }

    const id = setTimeout(() => {
      syncTimers.delete(player);
      syncPlayer(player, attempt);
    }, SETTLE_MS);

    syncTimers.set(player, { id, attempt });
  }

  /* ------------------------------------------------------------------ *
   * Customer input
   * ------------------------------------------------------------------ */

  function onChange(event) {
    const input = event.target;
    const meta = parseTypeInput(input);

    if (!meta) {
      return;
    }

    // Ignore our own synchronisation clicks.
    if (syncingPlayers.has(meta.player)) {
      return;
    }

    // Ignore fields Globo has switched off.
    if (input.disabled) {
      return;
    }

    savePlayerState(meta.player, meta.sewn);
  }

  /* ------------------------------------------------------------------ *
   * Observation
   * ------------------------------------------------------------------ */

  function collectFromNode(node, players) {
    if (!(node instanceof Element)) {
      return;
    }

    if (node.matches(INPUT_SELECTOR)) {
      const meta = parseTypeInput(node);
      if (meta) players.add(meta.player);
    }

    node.querySelectorAll(INPUT_SELECTOR).forEach(input => {
      const meta = parseTypeInput(input);
      if (meta) players.add(meta.player);
    });
  }

  function onMutations(mutations) {
    const affected = new Set();
    let structureChanged = false;

    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        /**
         * A renamed field invalidates both the parse cache
         * and the index.
         */
        if (mutation.attributeName === "data-field-name") {
          metaCache.delete(mutation.target);
          structureChanged = true;
        }

        const meta = parseTypeInput(mutation.target);

        if (meta) {
          affected.add(meta.player);
        }

        continue;
      }

      if (mutation.type === "childList") {
        if (mutation.addedNodes.length || mutation.removedNodes.length) {
          structureChanged = true;
        }

        mutation.addedNodes.forEach(node =>
          collectFromNode(node, affected)
        );
      }
    }

    if (structureChanged) {
      invalidateIndex();
    }

    affected.forEach(player => schedulePlayerSync(player));
  }

  /* ------------------------------------------------------------------ *
   * Lifecycle
   * ------------------------------------------------------------------ */

  function findRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function seedInitialState() {
    getIndex().forEach((entry, player) => {
      const mode = getActiveMode(player);

      if (mode === MODE.REGULAR) {
        savePlayerState(player, false);
      } else if (mode === MODE.SEWN) {
        savePlayerState(player, true);
      }
    });
  }

  function teardown() {
    if (rootPollId !== null) {
      clearInterval(rootPollId);
      rootPollId = null;
    }

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    document.removeEventListener("change", onChange);
    document.removeEventListener("shopify:section:unload", teardown);

    syncTimers.forEach(timer => clearTimeout(timer.id));
    syncTimers.clear();

    window.__playerTypeSync = false;
  }

  function init(container) {
    root = container;

    seedInitialState();

    document.addEventListener("change", onChange);
    document.addEventListener("shopify:section:unload", teardown);

    observer = new MutationObserver(onMutations);

    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["disabled", "data-field-name"]
    });

    window.__playerTypeSync = true;
  }

  /**
   * Wait for Globo to render, then start. If .gpo-app never
   * appears, this page isn't a Globo page and nothing runs:
   * no listeners, no observer, no state.
   */
  function bootstrap() {
    const immediate = findRoot();

    if (immediate) {
      init(immediate);
      return;
    }

    const deadline = Date.now() + ROOT_WAIT_MS;

    rootPollId = setInterval(() => {
      const container = findRoot();

      if (container) {
        clearInterval(rootPollId);
        rootPollId = null;
        init(container);
        return;
      }

      if (Date.now() >= deadline) {
        clearInterval(rootPollId);
        rootPollId = null;
      }
    }, ROOT_POLL_MS);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
  } else {
    bootstrap();
  }
})();